import { useId, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import AnswerStyleEditor from '@/components/answerStyle/AnswerStyleEditor';
import type { AnswerStyle } from '@/components/chat/AnswerStyle';
import { useNotification } from '@/components/ui-library/NotificationProvider';
import { useAnswerStyle } from '@/hooks/useAnswerStyle';
import useAnswerStyleActions, {
  type AnswerStyleSaveResult,
  type AnswerStyleSource,
} from '@/hooks/useAnswerStyleActions';
import { semanticColors } from '@/theme/tokens';

import { GRADIENT_MARK_SX, PRIMARY_PILL_SX, QUIET_BUTTON_SX } from './answerStyle.styles';

const SAVED_NOTE_KEY: Record<AnswerStyleSaveResult, string> = {
  account: 'answerStyle.note.accountSaved',
  browser: 'answerStyle.note.browserSaved',
  memory: 'answerStyle.note.visitOnly',
};

// Above the app chrome (1600–1900) and the mobile ballot pane, like LoginModal.
const Z_INDEX = 2000;

interface AnswerStyleDialogProps {
  open: boolean;
  source: AnswerStyleSource;
  onClose: () => void;
  onExited: () => void;
}

/**
 * "Answers, your way" — the optional editor, opened only when the voter asks
 * for it (the invitation, Response options). A dialog on desktop and a bottom
 * sheet on a phone; both are MUI modals, so focus is trapped inside, Escape
 * closes, and focus returns to the control that opened it.
 *
 * Nothing changes until "Use these choices" succeeds: Escape, the backdrop,
 * the close button, and "Not now" all leave the saved style as it was, and a
 * failed save keeps the editor open with the earlier choice still in force.
 * Mirrors the mobile `AnswerStyleSheet`.
 */
export default function AnswerStyleDialog({
  open,
  source,
  onClose,
  onExited,
}: AnswerStyleDialogProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  // Live query, so an embed iframe whose width settles after mount still gets
  // the right surface.
  const asSheet = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const snapshot = useAnswerStyle();
  const { save, dismissInvite, signedIn } = useAnswerStyleActions();
  const { showNotification } = useNotification();
  // The provider remounts this on every opening, so each one starts from the
  // saved style rather than a previous visit's draft.
  const [draft, setDraft] = useState<AnswerStyle>(snapshot.style);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const titleId = useId();
  const introId = useId();

  const close = (reason: 'notNow' | 'dismiss') => {
    if (saving) return;
    dismissInvite(reason === 'notNow' ? 'invite' : 'editor');
    onClose();
  };

  const handleUse = async () => {
    if (saving) return;
    setSaving(true);
    setFailed(false);
    try {
      const result = await save(draft, source);
      setSaving(false);
      onClose();
      showNotification(t(SAVED_NOTE_KEY[result]), 'success');
    } catch {
      setSaving(false);
      setFailed(true);
    }
  };

  const note = signedIn
    ? t('answerStyle.note.accountBefore')
    : snapshot.guestStorage === 'memory'
      ? t('answerStyle.note.visitOnly')
      : t('answerStyle.note.browserBefore');

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Header and actions stay put; the questions scroll between them, so
        // the primary and skip buttons are reachable at any text size.
        maxHeight: asSheet ? '92dvh' : 'min(720px, calc(100dvh - 64px))',
        minHeight: 0,
      }}
    >
      {asSheet && (
        <Box
          aria-hidden="true"
          sx={{
            alignSelf: 'center',
            width: 36,
            height: 4,
            mt: '8px',
            borderRadius: '999px',
            backgroundColor: semanticColors.borderLight,
          }}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          px: '20px',
          pt: '18px',
          pb: '12px',
        }}
      >
        <Box sx={GRADIENT_MARK_SX} aria-hidden="true">
          <SparklesIcon style={{ width: 16, height: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            id={titleId}
            component="h2"
            sx={{
              fontSize: 17,
              fontWeight: 700,
              lineHeight: 1.3,
              color: semanticColors.textPrimary,
            }}
          >
            {t('answerStyle.title')}
          </Typography>
          <Typography
            id={introId}
            sx={{ mt: '2px', fontSize: 13, lineHeight: 1.4, color: semanticColors.textCaption }}
          >
            {t('answerStyle.intro')}
          </Typography>
        </Box>
        <IconButton
          onClick={() => close('dismiss')}
          aria-label={t('answerStyle.actions.close')}
          sx={{
            mt: '-4px',
            mr: '-6px',
            width: 32,
            height: 32,
            color: semanticColors.textCaption,
            '&:hover': { backgroundColor: semanticColors.surfaceSubtle },
            '&:focus-visible': {
              outline: `2px solid ${semanticColors.brandPrimary}`,
              outlineOffset: '2px',
            },
          }}
        >
          <XMarkIcon aria-hidden="true" style={{ width: 18, height: 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: '20px', pb: '16px', overflowY: 'auto', minHeight: 0, flex: '1 1 auto' }}>
        <AnswerStyleEditor value={draft} onChange={setDraft} />
        {source !== 'invite' && (
          <Typography sx={{ mt: '12px', fontSize: 12, color: semanticColors.textCaption }}>
            {t('answerStyle.note.appliesNext')}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          px: '20px',
          pt: '12px',
          pb: asSheet ? 'calc(16px + env(safe-area-inset-bottom))' : '16px',
          borderTop: `1px solid ${semanticColors.borderLight}`,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Box
          role="status"
          aria-live="polite"
          aria-atomic="true"
          sx={{ flex: '1 1 180px', minWidth: 0 }}
        >
          <Typography
            sx={{
              fontSize: 12,
              lineHeight: 1.4,
              // `dangerHover` (#dc2626) clears 4.5:1 at this size; `danger` does not.
              color: failed ? semanticColors.dangerHover : semanticColors.textCaption,
            }}
          >
            {failed ? t('answerStyle.error') : note}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: '4px', marginInlineStart: 'auto' }}>
          <Button onClick={() => close('notNow')} aria-disabled={saving} sx={QUIET_BUTTON_SX}>
            {t('answerStyle.actions.notNow')}
          </Button>
          <Button
            onClick={() => void handleUse()}
            aria-disabled={saving}
            variant="contained"
            disableElevation
            startIcon={
              saving ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : undefined
            }
            sx={PRIMARY_PILL_SX}
          >
            {saving ? t('answerStyle.actions.saving') : t('answerStyle.actions.use')}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  if (asSheet) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => close('dismiss')}
        slotProps={{
          paper: {
            role: 'dialog',
            'aria-modal': true,
            'aria-labelledby': titleId,
            'aria-describedby': introId,
            sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16 },
          },
          transition: { onExited },
        }}
        sx={{ zIndex: Z_INDEX }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={() => close('dismiss')}
      aria-labelledby={titleId}
      aria-describedby={introId}
      maxWidth={false}
      slotProps={{
        transition: { onExited },
        paper: {
          sx: {
            width: 480,
            maxWidth: 'calc(100vw - 32px)',
            m: '16px',
            borderRadius: '16px',
            boxShadow: '0 12px 40px rgba(12, 47, 55, 0.18)',
          },
        },
      }}
      sx={{ zIndex: Z_INDEX }}
    >
      {content}
    </Dialog>
  );
}
